import { ChangeDetectionStrategy, Component, computed, inject, input, TemplateRef, viewChild } from '@angular/core';
import { IOfferCard, IOfferTier } from '../../types';
import { TinyColor } from '@ctrl/tinycolor';
import { BucketPipe } from "../../bucket.pipe";
import { ImageFallbackDirective } from '../../image-fallback.directive';
import { DateTime } from 'luxon'
import { MatIcon } from '@angular/material/icon';
import { NgTemplateOutlet } from '@angular/common';
import { ConfirmPopup } from '../../confirm.popup';

@Component({
  selector: 'loyalty-card',
  imports: [BucketPipe, ImageFallbackDirective, MatIcon, NgTemplateOutlet],
  templateUrl: './offer-loyalty-card.component.html',
  styleUrl: './offer-loyalty-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  host: {
    '[style.--shade-1]': 'shade1()',
    '[style.--shade-2]': 'shade2()',
    '[style.--shade-3]': 'shade3()',
    '[style.--shade-4]': 'shade4()',
    '[class.fitted]': 'fitted()',
  }
})
export class OfferLoyaltyCardComponent {

  offer = input.required<IOfferCard>();
  branded = input<boolean>(false);
  fitted = input<boolean>(false);

  confirm = inject(ConfirmPopup);

  tree = computed(() => this.offer().tier_tree!);
  brand = computed(() => this.offer().brand);

  tinyColor = computed(() => {
    return new TinyColor(this.brand().color);
  });

  isBright = computed(() => {
    return this.tinyColor().getLuminance() > 0.6;
  });

  clock_states = [
    '',//completed
    'clock_loader_90',
    'clock_loader_80',
    'clock_loader_60',
    'clock_loader_40',
    'clock_loader_20',
    'clock_loader_10',
  ]

  shades = computed(() => {
    return this.isBright() ? [
      this.darken(60),
      this.darken(40),
      this.darken(20),
      this.tinyColor().toHexString()
    ] : [
      this.tinyColor().toHexString(),
      this.lighten(20),
      this.lighten(40),
      this.lighten(60),
    ]
  });

  shade1 = computed(() => this.shades()[0]);
  shade2 = computed(() => this.shades()[1]);
  shade3 = computed(() => this.shades()[2]);
  shade4 = computed(() => this.shades()[3]);

  lighten(percent: number){
    if(percent > 1){
      percent = (Math.min(100, Math.max(0, percent)) / 100);
    }
    const tc = this.tinyColor();

    return new TinyColor({ 
      a: tc.a, 
      r: tc.r + Math.round((255 - tc.r) * percent), 
      g: tc.g + Math.round((255 - tc.g) * percent), 
      b: tc.b + Math.round((255 - tc.b) * percent) 
    }).toHexString();
  }

  darken(percent: number){

    if(percent > 1){
      percent = 1 - (Math.min(100, Math.max(0, percent)) / 100);
    }
    
    const tc = this.tinyColor();

    return new TinyColor({ 
      a: tc.a, 
      r: Math.round(tc.r * percent), 
      g: Math.round(tc.g * percent), 
      b: Math.round(tc.b * percent) 
    }).toHexString();
  }

  stepCount = computed(() => {
    return Math.max(...this.tree().tiers.map(tier => tier.redemptions_required + tier.redemptions_given));
  });

  columnCount = computed(() => {
    const mod4 = this.stepCount() % 4;
    const mod3 = this.stepCount() % 3;

    if(mod4 == 0){
      return 4;
    } else if(mod3 == 0){
      return 3;
    } else {
      return mod4 >= mod3 ? 4 : 3;
    }
  });

  timer = computed(() => {
    const { tiered_progress } = this.offer();

    if(tiered_progress){
      const timeLeft = Math.max(0, Math.min(6, Math.ceil(DateTime.fromISO(tiered_progress.usable_on).diffNow('hours').as('hours'))));
      return timeLeft;
    }

    return 0;
  });

  steps = computed(() => {

    const steps: Partial<IOfferCard>[] = Array(this.stepCount()).fill({}).map(v => Object.assign({}, this.offer()));

    (this.tree().tiers||[]).forEach(tier => {
      const rewardIndexes = Array(tier.redemptions_given).fill(1).map((slot,i) => tier.redemptions_required + i);

      const tierToMerge: Partial<IOfferTier> = {...tier};
      tierToMerge.tier = tier._id;
      delete tierToMerge._id;

      rewardIndexes.forEach(ri => Object.assign(steps[ri],tierToMerge));
    });

    return steps;
  });

  nextReward = computed(() => {
    const steps = this.steps() || [];
    const tier_progress = this.offer()?.tiered_progress || { progress: 0 };

    return steps.findIndex((step, i) => (i+1) > tier_progress.progress && !!step.tier) - tier_progress.progress;

  });

  rows = computed(() => {

    const columns = this.columnCount();
    const steps = this.steps();

    let rows = [];

    for(let i =0; i < steps.length; i+= columns){
      rows.push(steps.slice(i, Math.min(i+columns, steps.length)));
    }

    return rows
  });

  popupContent = viewChild<TemplateRef<any>>('popup');

  moreInfo(){
    if(this.popupContent()){
      this.confirm.open('Loyalty Rewards', this.popupContent()!, 'Loyalty Information', [
        {
          label: 'Got it!',
          paint: 'dark',
          close: true
        }
      ], null);
    }
  }

}

